import { IFormFieldMergeConfig } from './form-field-merge-config.interface';
import { FormValidatorType } from './form-validator-type';
import { InputType } from './input-type';

/**
 * Venezuelan Phone Number (Pago Movil) merge configuration.
 *
 * BE fields:
 *   - VirtualPaymentAccountNoPrefix (OptionList: 0412, 0414, 0416, 0424, 0426)
 *   - VirtualPaymentAccountNo (Text: 7-digit regex)
 *
 * Merged UI field:
 *   - Single text input accepting 11-digit VE mobile number (e.g. 04125515168)
 *
 * Normalization handles pasted international formats:
 *   "+58 412-5515168" → "04125515168"
 *   "584125515168"    → "04125515168"
 *   "04125515168"     → "04125515168" (no-op)
 */
export const VE_PAGO_MOVIL_PHONE_CONFIG: IFormFieldMergeConfig = {
  prefixFieldId: 'virtualPaymentAccountNoPrefix',
  valueFieldId: 'virtualPaymentAccountNo',

  validPrefixes: ['0412', '0414', '0416', '0424', '0426'],
  prefixLength: 4,

  normalize: (rawInput: string): string => {
    // Same trim logic as login (utils-provider.ts cleanPhoneNumber):
    // strip spaces, hyphens, parens, plus signs.
    let cleaned = rawInput.replace(/[ \-()+]/g, '');

    // Add the 0: replace Venezuelan country code (58) with local prefix (0)
    // e.g. "584125515168" → "04125515168"
    if (cleaned.startsWith('58') && cleaned.length > 10) {
      cleaned = '0' + cleaned.substring(2);
    }

    return cleaned;
  },

  split: (mergedValue: string) => ({
    prefix: mergedValue.substring(0, 4),
    value: mergedValue.substring(4),
  }),

  mergedValidators: [
    { type: FormValidatorType.Required },
    { type: FormValidatorType.Regex, regex: /^0(412|414|416|424|426)\d{7}$/ },
    { type: FormValidatorType.MinLength, minLength: 11 },
    { type: FormValidatorType.MaxLength, maxLength: 11 },
  ],

  mergedInputType: InputType.Text,
  mergedVisualMask: '0000 0000000',
  mergedHint: 'page.sendMoneyRecipientBankDepositPage.pagoMovilPhoneHint',
  mergeHintTranslated: false,
};

/** Bre-B (Colombia) mobile-number config: applies +57 prefix on submit when breBIDType is Mobile. */
export const BREB_MOBILE_CONFIG: IFormFieldMergeConfig = {
  valueFieldId: 'phoneNumber',
  beFieldId: 'breB',
  constantPrefix: '+57',
  triggerFieldId: 'breBIDType',
  triggerValue: '111901',

  normalize: (rawInput: string): string => {
    let cleaned = rawInput || '';
    if (cleaned.startsWith('+57')) {
      cleaned = cleaned.substring(3);
    } else if (cleaned.startsWith('57') && cleaned.length > 10) {
      cleaned = cleaned.substring(2);
    }
    return cleaned.replace(/[^0-9]/g, '');
  },

  split: (mergedValue: string) => ({
    prefix: '+57',
    value: mergedValue && mergedValue.startsWith('+57') ? mergedValue.substring(3) : (mergedValue || ''),
  }),

  mergedValidators: [
    { type: FormValidatorType.Required },
    { type: FormValidatorType.MinLength, minLength: 5 },
    { type: FormValidatorType.MaxLength, maxLength: 64 },
    { type: FormValidatorType.BreBError },
  ],

  mergedInputType: InputType.Text,
};

/**
 * All available merge configs, in priority order.
 * Consumers pass this to FormFieldMergeTransformer.findMatchingConfig() to auto-detect
 * which config applies to a given set of form fields.
 */
export const FORM_FIELD_MERGE_CONFIGS: IFormFieldMergeConfig[] = [
  VE_PAGO_MOVIL_PHONE_CONFIG,
  BREB_MOBILE_CONFIG,
];



// WEBPACK FOOTER //
// ./src/providers/formbuilder/form-field-merge-configs.ts